import React, { useCallback, useEffect, useRef, useState } from "react";

import { Box, BoxProps, SxProps } from "@mui/material";

import ListTabs, { ListTabsProps } from "@/components/ListTabs";
import Zoom from "@/components/Zoom";

import { Ad, Layout } from "@/extensions/obituary/types";

import useSessionStorage from "@/hooks/useSessionStorage";

/**
 * Easing function for quadratic easing in and out.
 * Makes the transition a bit smoother.
 */
function easeInOutQuad(x: number): number {
  return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
}

const rootSx: SxProps = {
  position: "relative",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  bgcolor: "background.default",
  ml: "0 !important",
};

const tabsSx: SxProps = {
  position: "absolute",
  top: 8,
  left: 8,
  zIndex: 8,
  width: "100%",
  overflowX: "scroll",
  paddingRight: 2,
};

const zoomSx: SxProps = {
  position: "absolute",
  bottom: 8,
  right: 8,
};

const imgContainerSx = (
  opacity: number,
  size: number,
  layout: Layout,
): SxProps => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: `translate(-50%, -50%) scale(${size})`,
  width: layout === "OBITUARY_TWO_COLUMN" ? "70mm" : "38mm",
  maxHeight: "100%",
  opacity,
});

const imgSx: SxProps = {
  height: "100%",
  width: "100%",
  bgcolor: "white",
};

interface PreviewProps
  extends Omit<BoxProps, "onChange">,
    Pick<ListTabsProps, "tabs"> {
  adId: Ad["id"];
  defaultSize?: number;
  duration?: number;
  layout?: Layout;
  src: string;
  sx?: SxProps;
  onTabChange: ListTabsProps["onChange"];
}

/**
 * Image preview that fades between two images.
 *
 * @param duration - Duration of the fade transition in milliseconds, default 200.
 */
const Preview: React.FC<PreviewProps> = ({
  adId,
  defaultSize = 1,
  duration = 150,
  layout = "OBITUARY_ONE_COLUMN",
  src,
  sx,
  tabs,
  onTabChange,
  ...props
}) => {
  const [oldSrc, setOldSrc] = useState(src);
  const [opacity, setOpacity] = useState(1);

  const [size, setSize] = useSessionStorage(
    "obituary-preview-size",
    defaultSize,
  );

  const interpolationRef = useRef<number>();

  useEffect(() => {
    if (typeof src !== "string") return;

    if (src !== oldSrc) {
      setOpacity(0);
      startInterpolation();
    }
  }, [src]);

  const startInterpolation = useCallback(() => {
    if (interpolationRef.current) {
      cancelAnimationFrame(interpolationRef.current);
    }

    const startTime = performance.now();

    const interpolate = (currentTime: number) => {
      const delta = currentTime - startTime;
      const progress = Math.min(delta / duration, 1);
      const ease = easeInOutQuad(progress);
      setOpacity(ease);

      if (ease < 1) {
        interpolationRef.current = requestAnimationFrame(interpolate);
      } else {
        setOldSrc(src);
      }
    };

    interpolationRef.current = requestAnimationFrame(interpolate);
  }, [duration, src]);

  const handleSizeChange = useCallback(
    (newSize: number) => setSize(newSize),
    [setSize],
  );

  return size == null ? null : (
    <Box sx={{ ...rootSx, ...sx }} {...props}>
      <ListTabs
        activeTab={adId.toString()}
        sx={tabsSx}
        tabs={tabs}
        onChange={onTabChange}
      />

      {oldSrc != null && oldSrc !== "" && (
        <Box sx={imgContainerSx(1 - opacity, size, layout)}>
          <Box component="img" src={oldSrc} sx={imgSx} />
        </Box>
      )}

      <Box sx={imgContainerSx(opacity, size, layout)}>
        <Box component="img" src={src} sx={imgSx} />
      </Box>

      <Zoom
        max={3}
        min={0.5}
        sx={zoomSx}
        zoom={size}
        onChange={handleSizeChange}
      />
    </Box>
  );
};

export default Preview;
